<script setup lang="ts">
    // @ts-expect-error raison: lib externe mal typée
    import { defineExpose, inject, reactive } from "vue";
    import FinancePrelevementsForm from "@/views/finance/@forms/FinancePrelevementsForm.vue";
    import { ref } from 'vue';
    import CustomStepper from "@/components/CustomStepper.vue";
    import CustomStepperStep from "@/components/CustomStepperStep.vue";
    import { AMOUNT_FORMAT_CONFIG, formatDateFrench, MONTHS_FR } from "@/utils";
    import axios from "axios";
    import { useToastStore } from "@/stores/toast.store";
    import moment from "moment";
import { useConfirmationStore } from "@/stores/confirmation.store";

    const toastStore = useToastStore();
    const confirmationStore = useConfirmationStore();
    const BACKEND_URL = inject('BACKEND_URL');
    const isOpened = ref(false);
    const loading = ref(false);
    const collectivite = ref(null as any);
    const customStepperRef = ref<InstanceType<typeof CustomStepper> | null>(null);
    let promise: Promise<boolean>;
    let resolvePromise: any;
    let rejectPromise: any;
    const startupInfos = ref({
        collectivite: null,
        periodes: []
    });

    const formData = reactive({
        taux_interet: 0,
        indexes: [],
        periode: '',
        periode_label: '',
        montant_expected: 0,
        montant_prelevement: 0,
        file: null as File | null,
        file_name: '',
        file_preview: null as any,
    });
    const errors = reactive({
        periode: "",
        montant_expected: "",
        montant_prelevement: "",
        file: "",
    });

    async function open(id: number) {
        await fetchStartupInfos(id);
        promise = new Promise((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
        });
        isOpened.value = true;
        return await promise;
    }

    async function fetchStartupInfos(id: number) {
        loading.value = true;
        try {
            const response = await axios.get(`/backoffice/collectivites/${id}/get_startupinfos_validation`);
            collectivite.value = response.data.collectivite;
            const periode = moment(response.data.periode);
            if (periode.isValid()) {
                formData.periode = periode.format('YYYY-MM-DD');
                formData.periode_label = MONTHS_FR[periode.month()] + ' ' + periode.year()
            }
            formData.montant_expected = response.data.montant_prelevement_expected;
            Object.assign(formData, {
                taux_interet: response.data.profil.taux_interet,
                indexes: response.data.profil.index && response.data.profil.index.length > 0 ? response.data.profil.index : Array.from({ length: 12 }, (_, index) => 0)
            });
        } catch(error: any) {
            toastStore.error(error.message || 'Une erreur est survenue lors de la requête au serveur.');
        } finally {
            loading.value = false;
        }
    }

    function onClose() {
        resolvePromise(false);
    }

    function onFileUploaded(event: any) {
        if (event.target.files.length > 0) {
            var reader = new FileReader();
            reader.onload = function(e) {
                formData.file = event.target.files[0];
                formData.file_name = event.target.files[0].name;
                formData.file_preview = e.target!.result as string;
                event.target.value = null;
            }
            reader.readAsDataURL(event.target.files[0]);
        }
    }

    function validate() {
        let isValid = true;
        errors.periode = '';
        if (!formData.periode) {
            errors.periode = "La période est requise";
            isValid = false;
        }

        /*errors.montant_expected = '';
        if (!formData.montant_expected || formData.montant_expected <= 0) {
            errors.montant_expected = "Le montant à récupérer est invalide";
            isValid = false;
        }

        errors.montant_prelevement = '';
        if (!formData.montant_prelevement || formData.montant_prelevement <= 0 || formData.montant_prelevement < formData.montant_expected) {
            errors.montant_prelevement = "Le montant du prélèvement est invalide";
            isValid = false;
        }*/

        errors.file = '';
        if (!formData.file) {
            errors.file = "Le fichier de prélèvement est requis";
            isValid = false;
        }

        return isValid;
    }

    async function submit() {
        const stepperEl = customStepperRef.value;
        if (!stepperEl) { return; }

        if (stepperEl.activeIndex == 0) { stepperEl.next(); }
        else {
            if (!validate()) { return; }

            const confirm = await confirmationStore.open('Vérouillage collectivité', 'Voulez-vous vraiment proceder au verouillage de cette collectivité ? Cette action est irreversible');
            if (!confirm) { return; }

            loading.value = true;
            try {
                const formToUpload = new FormData();
                formToUpload.append("collectivite_id", collectivite.value.id.toString());
                formToUpload.append("taux_interet", formData.taux_interet.toString());
                formToUpload.append("indexes", JSON.stringify(formData.indexes));
                formToUpload.append("periode_prelevement", formData.periode);
                formToUpload.append("montant_prelevement", formData.montant_prelevement.toString());
                formToUpload.append("fichier_prelevement", formData.file!, formData.file!.name);
                await axios.post(`/backoffice/collectivites/${collectivite.value.id}/validate_collectivite`, formToUpload);
                toastStore.success('La collectivité a été verouillée');
                resolvePromise(true);
                isOpened.value = false;
            } catch(error: any) {
                toastStore.error(error.message || 'Une erreur est survenue lors de la requête au serveur.');
            } finally {
                loading.value = false;
            }
        }
        
    }

    function previous() {
        const stepperEl = customStepperRef.value;
        if (!stepperEl) { return; }

        stepperEl.previous();
    }

    defineExpose({ open, close });
</script>

<template>
    <custom-modal v-model:show="isOpened" @closed="onClose" :classesNames="'modal-fullscreen'">
        <template #header><h4 class="font-semibold text-primary-color">Validation du dossier de collectivité</h4></template>
        <div>
                    
            <section class="s20_mobile px-4">

            <div class="s20 widget flex align-items-center px-1 py-5 my-5" v-if="collectivite">
                <div class="s2_mobile h-100p px-7">
                    <div class="rounded-100 overflow-hidden item active"><!--active-->
                        <img :src="collectivite.logo ? (BACKEND_URL + '/assets/collectivites/' + collectivite.logo) : '/assets/logos/logo2.svg'" width="100%" />
                    </div>
                </div>
                <div class="s3_mobile h-100p px-2">
                    <h4 class="mb-2">{{ collectivite.nom || '' }}</h4>
                    <h3>{{ collectivite.rccm || 'N/A' }}</h3>
                </div>
                <div class="s3_mobile h-100p px-2">
                    <h4 class="font-normal mb-5">{{ '0 / 0 Membres' }}</h4>
                    <span class="tag-state warning stroke">En cours</span>
                    <span class="tag-state notif stroke">Actif</span>
                </div>
                <div class="s4_mobile h-100p px-3">
                    <h4 class="font-normal">
                        Date de création <br class="mb-2"/>
                        <b class="text-2xl">{{ formatDateFrench(collectivite.created_at) }}</b>
                    </h4>
                </div>
                <div class="s8_mobile h-100p px-5">
                    <!--Barre de progression - Component-->
                    <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                        <div class="widget-floating py-0 text-xl flex-right">Membres {{ '0' }}%</div>
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" :style="{ width: '0%' }">
                            0 / 0
                        </div>
                    </div>
                    <!--End Barre de progression-->
                </div>
            </div>
                            <div class="clear-both"></div>

                <div class="widget px-7 py-7 my-5">

                    <custom-stepper ref="customStepperRef">
                        <custom-stepper-step title="Vérification du profil financier">
                            <div class="s10_mobile container-full-2"> 
                                <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                                    <img src="/public/icons/icon-info-perso.svg" class="size-l" alt="" />
                                    Profil Financier
                                </h2>
                            </div> 
                            <div class="clear-both h-5"></div>

                            <div class="s20 items-center gap-6 px-5 pb-6 collapse-content active">
                                <!-- <h3 class="mb-2 px-5">Retrait de carte</h3> -->
                                <div class="s20 px-4">
                                    <div class="form-field-floating lg reactive my-4">
                                        <money3 class="form-input" v-model="formData.taux_interet" name="profil.taux_interet" id="profil.taux_interet" placeholder="" v-bind="AMOUNT_FORMAT_CONFIG"></money3>
                                        <label for="taux_interet">Taux Interets Microfinance <b class="color-danger">*</b></label>
                                    </div>
                                </div>
                                <div class="grid6 grid-row-gap-0 px-4 my-1">
                                    <div class="form-field-floating lg reactive my-4" v-for="(indexEl, i) of formData.indexes" :key="'index_' + (i + 1)">
                                        <input type="number" class="form-input" name="index[]" :id="'index_' + (i + 1)" placeholder="" v-model="formData.indexes[i]">
                                        <label :for="'index_' + (i + 1)">Index {{ i + 1 }} <b class="color-danger">*</b></label>
                                    </div>
                                </div>
                            </div>
                        </custom-stepper-step>
                        <custom-stepper-step title="Effectuer le 1er prélèvement">
                            <div class="s10_mobile container-full-2"> 
                                <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                                    <img src="/public/icons/icon-info-perso.svg" class="size-l" alt="" />
                                    Enregistrer le prélèvement
                                </h2>
                            </div> 
                            <div class="clear-both h-5"></div>

                            <div class="s20 items-center gap-6 px-5 pb-6 collapse-content active">
                                <!-- <h3 class="mb-2 px-5">Retrait de carte</h3> -->
                                <div class="s20 px-4">
                                    <div class="form-field-floating lg reactive my-4">
                                        <input type="text" class="form-input" name="periode_prelevement" id="periode_prelevement" placeholder="" :value="formData.periode_label" :readonly="true">
                                        <label for="periode_prelevement">Période <b class="color-danger">*</b></label>
                                    </div>
                                    <span v-if="!!errors['periode']" class="text-red">{{ errors['periode'] }}</span>
                                </div>
                                <div class="s20 px-4">
                                    <div class="form-field-floating lg reactive my-4">
                                        <input type="number" class="form-input" name="montant_decaissement_expected" id="montant_decaissement_expected" placeholder="" :value="formData.montant_expected" :readonly="true">
                                        <label for="montant_decaissement_expected">Montant à recevoir <b class="color-danger">*</b></label>
                                    </div>
                                    <span v-if="!!errors['montant_expected']" class="text-red">{{ errors['montant_expected'] }}</span>
                                </div>
                                <div class="s20 px-4">
                                    <div class="form-field-floating lg reactive my-4">
                                        <input type="number" class="form-input" name="montant_decaissement_expected" id="montant_decaissement_expected" placeholder="" v-model="formData.montant_prelevement">
                                        <label for="montant_decaissement_expected">Montant du virement <b class="color-danger">*</b></label>
                                    </div>
                                    <span v-if="!!errors['montant_prelevement']" class="text-red">{{ errors['montant_prelevement'] }}</span>
                                </div>
                                <div class="s20 px-4">
                                    <label for="">Fichier de virement <b class="color-danger">*</b></label>
                                    <label for="file-prelevement" class="mt-4 custom-upload" :class="{'active': formData.file}"><!--active-->
                                        <div class="custom-upload-st2">
                                            <input id="file-prelevement" class="display-none" type="file" @change="onFileUploaded($event)" accept=".pdf" />
                                            <div class="s4 custom-upload-img">.PDF</div>
                                            <div class="s12">
                                                <span id="file-upload-title" class="custom-upload-text">{{ formData.file_name ? formData.file_name : 'Charger le fichier' }}</span> <b class="color-danger">*</b><br>
                                                <span id="file-upload-title" class="custom-upload-text">{{ formData.file_name ? 'Doc chargé' : 'Uploader le doc' }}</span>
                                            </div>
                                            <div class="s4 right"><img src="/icons/icon-upload.svg" class="custom-upload-icon" /></div>
                                        </div>
                                    </label>
                                    <span v-if="!!errors['file']" class="text-red">{{ errors['file'] }}</span>
                                </div>
                            </div>
                        </custom-stepper-step>
                    </custom-stepper>
                    

                    <div class="clear-both"></div>
                    <div style="display: flex; justify-content: space-between;">
                        <custom-button :classesList="'btn-ultra lg shadow-0'" @click="previous" v-if="customStepperRef && customStepperRef.activeIndex > 0">
                            <span>Précédent</span>
                        </custom-button>
                        <div style="flex: 1">&nbsp;</div>
                        <custom-button :classesList="'btn-ultra lg shadow-0'" @click="submit" :loading="loading">
                            <template v-if="customStepperRef && customStepperRef.activeIndex == 0">
                                <span>Suivant</span>
                            </template>
                            <template v-if="customStepperRef && customStepperRef.activeIndex == 1">
                                <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                <span>Valider</span>
                            </template>
                        </custom-button>
                    </div>
                    
                    
                    <div class="clear-both"></div>
                </div>
            </section>
        </div>
        <template #footer>&nbsp;</template>
    </custom-modal>
</template>